/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import style from './screenmaker.module.css';
import { Table, Input, Pagination, Space, Dropdown, Checkbox, Tag, Button, Tabs } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { CiSearch } from 'react-icons/ci';
import FormModal from '../../components/Modal/FormModal';
import Header from '../../components/Header/header';
import * as Action from '../../store/slices/screenmaker/screenmaker';
import { connect } from 'react-redux';
import TransactionModal from './TransactionModal';
import ApproveModal from './ApproveModal';
import RejectModal from './RejectModal';
import { hasPermission } from '../../utils/premission';
import { PERMISSIONS } from '../../utils/constant/permissions';

interface Api {
    ScreenMaker_Red: any;
}

interface DataSource {
    key: string;
    fileId: string;
    fileName: string;
    Status: string;
    createdByUser: string;
    csvTemplateName: string;
    createdAt: string;
    approvedByUser: string;
    rejectedByUser: string;
}

const ScreenMakerTable: React.FC<Api> = ({ ScreenMaker_Red }) => {
    const [activeTab, setActiveTab] = useState<string>('pending');

    // --- Pending tab state ---
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(9);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(['fileId', 'fileName', 'Status']);
    const [loadingText, setLoadingText] = useState<boolean>(true);
    const [open, setOpen] = useState(false);
    const [modalText] = useState<string>('');
    const [tableData, setTableData] = useState<DataSource[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [approveModalVisible, setApproveModalVisible] = useState(false);
    const [approveFileId, setApproveFileId] = useState<number | null>(null);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectFileId, setRejectFileId] = useState<number | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [transactionModalVisible, setTransactionModalVisible] = useState(false);
    const [selectedFileId, setSelectedFileId] = useState<number | null>(null);

    // --- Uploaded (approved/rejected) tab state ---
    const [uploadedPage, setUploadedPage] = useState<number>(1);
    const [uploadedPageSize, setUploadedPageSize] = useState<number>(9);
    const [uploadedData, setUploadedData] = useState<DataSource[]>([]);
    const [uploadedTotal, setUploadedTotal] = useState<number>(0);
    const [uploadedLoading, setUploadedLoading] = useState<boolean>(false);
    const [uploadedSearch, setUploadedSearch] = useState('');
    const [uploadedDebouncedSearch, setUploadedDebouncedSearch] = useState('');
    const [uploadedRefreshKey, setUploadedRefreshKey] = useState(0);

    // Debounce pending search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), 500);
        return () => clearTimeout(t);
    }, [searchText]);

    // Debounce uploaded search
    useEffect(() => {
        const t = setTimeout(() => setUploadedDebouncedSearch(uploadedSearch), 500);
        return () => clearTimeout(t);
    }, [uploadedSearch]);

    // Fetch pending files
    useEffect(() => {
        fetchPendingFiles(currentPage, pageSize);
    }, [currentPage, pageSize, debouncedSearch, refreshKey]);

    // Fetch uploaded files
    useEffect(() => {
        fetchUploadedFiles(uploadedPage, uploadedPageSize);
    }, [uploadedPage, uploadedPageSize, uploadedDebouncedSearch, uploadedRefreshKey]);

    const fetchPendingFiles = (page: number, size: number) => {
        setLoadingText(true);
        const token = localStorage.getItem('loginToken');
        fetch(`${process.env.REACT_APP_API_URL}/file/get-uploaded-files/0/10000`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(res => res.json())
            .then(res => {
                const list = Array.isArray(res) ? res : res?.content || [];
                const mapped = list
                    .filter((item: any) => !item.isApproved && !item.isRejected)
                    .map((item: any) => ({
                        key: String(item.fileId),
                        fileId: item.fileId,
                        fileName: item.fileName || '-',
                        Status: 'Pending',
                        createdByUser: item.createdByUser || '-',
                        csvTemplateName: item.csvTemplateName || '-',
                        createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString() : '-',
                        approvedByUser: item.approvedByUser || '-',
                        rejectedByUser: item.rejectedByUser || '-',
                    }));
                setTableData(mapped);
                setTotal(mapped.length);
            })
            .catch(() => { setTableData([]); setTotal(0); })
            .finally(() => setLoadingText(false));
    };

    const fetchUploadedFiles = (page: number, size: number) => {
        setUploadedLoading(true);
        const token = localStorage.getItem('loginToken');
        fetch(`${process.env.REACT_APP_API_URL}/file/get-uploaded-files/0/10000`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(res => res.json())
            .then(res => {
                const list = Array.isArray(res) ? res : res?.content || [];
                const mapped = list.map((item: any) => ({
                    key: String(item.fileId),
                    fileId: item.fileId,
                    fileName: item.fileName || '-',
                    Status: item.isApproved ? 'Approved' : item.isRejected ? 'Rejected' : 'Pending',
                    createdByUser: item.createdByUser || '-',
                    csvTemplateName: item.csvTemplateName || '-',
                    createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString() : '-',
                    approvedByUser: item.approvedByUser || '-',
                    rejectedByUser: item.rejectedByUser || '-',
                })).filter((item: any) => item.Status === 'Approved' || item.Status === 'Rejected');
                setUploadedData(mapped);
                setUploadedTotal(mapped.length);
            })
            .catch(() => { setUploadedData([]); setUploadedTotal(0); })
            .finally(() => setUploadedLoading(false));
    };

    const handleCheckboxChange = (checked: boolean, column: string) => {
        setSelectedColumns(prev =>
            checked ? [...prev, column] : prev.filter(c => c !== column)
        );
    };

    // File columns for pending tab (with Approve/Reject actions)
    const pendingColumns = [
        {
            title: 'File ID',
            dataIndex: 'fileId',
            key: 'fileId',
            width: 100,
        },
        {
            title: 'File Name',
            dataIndex: 'fileName',
            key: 'fileName',
            width: 300,
        },
        {
            title: 'Status',
            dataIndex: 'Status',
            key: 'Status',
            width: 110,
            render: (status: string) => (
                <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Created By User',
            dataIndex: 'createdByUser',
            key: 'createdByUser',
            width: 130,
            render: (val: string) => val || '-',
        },
        {
            title: 'Template Name',
            dataIndex: 'csvTemplateName',
            key: 'csvTemplateName',
            width: 150,
            render: (val: string) => val || '-',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 160,
            render: (val: string) => val || '-',
        },
        {
            title: 'Action',
            key: 'action',
            width: 160,
            render: (_: any, record: DataSource) =>
                hasPermission(PERMISSIONS.TRX_UPLOAD_APPROVE) ? (
                    <Space>
                        <Button
                            type="primary"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setApproveFileId(Number(record.fileId));
                                setApproveModalVisible(true);
                            }}
                        >
                            Approve
                        </Button>
                        <Button
                            danger
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setRejectFileId(Number(record.fileId));
                                setRejectModalVisible(true);
                            }}
                        >
                            Reject
                        </Button>
                    </Space>
                ) : null,
        },
    ];

    // File columns for uploaded tab (approved/rejected — no action buttons)
    const uploadedColumns = [
        {
            title: 'File ID',
            dataIndex: 'fileId',
            key: 'fileId',
            width: 100,
        },
        {
            title: 'File Name',
            dataIndex: 'fileName',
            key: 'fileName',
            width: 300,
        },
        {
            title: 'Status',
            dataIndex: 'Status',
            key: 'Status',
            width: 120,
            render: (status: string) => {
                const s = (status || '').toLowerCase();
                const color = s === 'approved' ? 'green' : s === 'rejected' ? 'red' : 'default';
                return (
                    <Tag color={color} style={{ marginInlineEnd: 0 }}>
                        {status.toUpperCase()}
                    </Tag>
                );
            },
        },
        {
            title: 'Created By User',
            dataIndex: 'createdByUser',
            key: 'createdByUser',
            width: 130,
            render: (val: string) => val || '-',
        },
        {
            title: 'Template Name',
            dataIndex: 'csvTemplateName',
            key: 'csvTemplateName',
            width: 150,
            render: (val: string) => val || '-',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 160,
            render: (val: string) => val || '-',
        },
        {
            title: 'Approved By',
            dataIndex: 'approvedByUser',
            key: 'approvedByUser',
            width: 130,
            render: (val: string) => val || '-',
        },
        {
            title: 'Rejected By',
            dataIndex: 'rejectedByUser',
            key: 'rejectedByUser',
            width: 130,
            render: (val: string) => val || '-',
        },
    ];

    const filteredPendingData = tableData.filter(item =>
        Object.values(item).some(val =>
            val.toString().toLowerCase().includes(debouncedSearch)
        )
    );

    // Client-side pagination slice for pending
    const paginatedPendingData = filteredPendingData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const filteredUploadedData = uploadedData.filter(item =>
        Object.values(item).some(val =>
            val.toString().toLowerCase().includes(uploadedDebouncedSearch)
        )
    );

    // Client-side pagination slice for uploaded
    const paginatedUploadedData = filteredUploadedData.slice(
        (uploadedPage - 1) * uploadedPageSize,
        uploadedPage * uploadedPageSize
    );

    const columnFilterItems = [
        { key: '1', label: <Checkbox checked={selectedColumns.includes('fileId')} onChange={(e) => handleCheckboxChange(e.target.checked, 'fileId')}>File ID</Checkbox> },
        { key: '2', label: <Checkbox checked={selectedColumns.includes('fileName')} onChange={(e) => handleCheckboxChange(e.target.checked, 'fileName')}>File Name</Checkbox> },
        { key: '3', label: <Checkbox checked={selectedColumns.includes('Status')} onChange={(e) => handleCheckboxChange(e.target.checked, 'Status')}>Status</Checkbox> },
    ];

    const onRefresh = () => {
        setRefreshKey(prev => prev + 1);
        setUploadedRefreshKey(prev => prev + 1);
    };

    return (
        <div className={style.sroll}>
            <Header />
            <FormModal
                modalTitle={modalText}
                Subtitle={modalText}
                open={open}
                setOpen={setOpen}
                onOk={() => {}}
                onCancel={() => {}}
            />
            <TransactionModal
                visible={transactionModalVisible}
                fileId={selectedFileId}
                onClose={() => setTransactionModalVisible(false)}
            />
            <ApproveModal
                visible={approveModalVisible}
                fileId={approveFileId}
                onClose={() => setApproveModalVisible(false)}
                onSuccess={onRefresh}
            />
            <RejectModal
                visible={rejectModalVisible}
                fileId={rejectFileId}
                onClose={() => setRejectModalVisible(false)}
                onSuccess={onRefresh}
            />

            <div className={style.Route}>
                <h5>Uploaded Files</h5>
            </div>

            <div className={style.container}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'pending',
                            label: 'Pending Approval',
                            children: (
                                <>
                                    <Space className={style.SearchTable}>
                                        <div className={style.SearchBar}>
                                            <Input
                                                placeholder="Search..."
                                                prefix={<CiSearch size={20} />}
                                                onChange={(e) => setSearchText(e.target.value.toLowerCase())}
                                                style={{ width: 200 }}
                                                className={style.inputSearch}
                                            />
                                            <Dropdown menu={{ items: columnFilterItems }} trigger={['click']}>
                                                <SettingOutlined className={style.tableSettings} />
                                            </Dropdown>
                                        </div>
                                    </Space>
                                    <Table
                                        loading={loadingText}
                                        columns={pendingColumns}
                                        dataSource={paginatedPendingData}
                                        pagination={false}
                                        rowSelection={{ type: 'checkbox' }}
                                        size="small"
                                        rowKey="key"
                                        onRow={(record) => ({
                                            onClick: () => {
                                                setSelectedFileId(Number(record.fileId));
                                                setTransactionModalVisible(true);
                                            },
                                            style: { cursor: 'pointer' },
                                        })}
                                    />
                                    <Pagination
                                        align="end"
                                        showTotal={(t) => `Total ${t} items`}
                                        current={currentPage}
                                        total={filteredPendingData.length}
                                        pageSize={pageSize}
                                        pageSizeOptions={[9, 18, 36, 72]}
                                        showSizeChanger
                                        onChange={(page, newSize) => {
                                            if (newSize !== pageSize) {
                                                setPageSize(newSize);
                                                setCurrentPage(1);
                                            } else {
                                                setCurrentPage(page);
                                            }
                                        }}
                                    />
                                </>
                            ),
                        },
                        {
                            key: 'uploaded',
                            label: 'Approved / Rejected',
                            children: (
                                <>
                                    <Space className={style.SearchTable}>
                                        <div className={style.SearchBar}>
                                            <Input
                                                placeholder="Search..."
                                                prefix={<CiSearch size={20} />}
                                                onChange={(e) => setUploadedSearch(e.target.value.toLowerCase())}
                                                style={{ width: 200 }}
                                                className={style.inputSearch}
                                            />
                                        </div>
                                    </Space>
                                    <Table
                                        loading={uploadedLoading}
                                        columns={uploadedColumns}
                                        dataSource={paginatedUploadedData}
                                        pagination={false}
                                        rowSelection={{ type: 'checkbox' }}
                                        size="small"
                                        rowKey="key"
                                        onRow={(record) => ({
                                            onClick: () => {
                                                setSelectedFileId(Number(record.fileId));
                                                setTransactionModalVisible(true);
                                            },
                                            style: { cursor: 'pointer' },
                                        })}
                                    />
                                    <Pagination
                                        align="end"
                                        showTotal={(t) => `Total ${t} items`}
                                        current={uploadedPage}
                                        total={filteredUploadedData.length}
                                        pageSize={uploadedPageSize}
                                        pageSizeOptions={[9, 18, 36, 72]}
                                        showSizeChanger
                                        onChange={(page, newSize) => {
                                            if (newSize !== uploadedPageSize) {
                                                setUploadedPageSize(newSize);
                                                setUploadedPage(1);
                                            } else {
                                                setUploadedPage(page);
                                            }
                                        }}
                                    />
                                </>
                            ),
                        },
                    ]}
                />
            </div>
        </div>
    );
};

function mapStateToProps(state: any) {
    return {
        ScreenMaker_Red: state.ScreenMaker_Red,
    };
}

export default connect(mapStateToProps, Action)(ScreenMakerTable);
